<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, provide } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { useMatchMedia } from '@/utils'
import MediaflowService from '@/services/MediaflowService'
import StreamStatsService from '@/services/StreamStatsService'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCloud from '@/components/icon/IconCloud.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import MediaflowEditorPanel from '@/components/Mediaflow/MediaflowEditorPanel.vue'
import MediaflowTransitionRail from '@/components/Mediaflow/MediaflowTransitionRail.vue'
import MediaflowMonitor from '@/components/Mediaflow/MediaflowMonitor.vue'
import MediaflowShortcuts from '@/components/Mediaflow/MediaflowShortcuts.vue'
import MediaflowScenesDock from '@/components/Mediaflow/MediaflowScenesDock.vue'
import MediaflowSourcesDock from '@/components/Mediaflow/MediaflowSourcesDock.vue'
import { useMediaflowSourceActions } from '@/components/Mediaflow/useMediaflowSourceActions'
import MediaflowGraphicsDock from '@/components/Mediaflow/MediaflowGraphicsDock.vue'
import MediaflowBreaksDock from '@/components/Mediaflow/MediaflowBreaksDock.vue'
import { useMediaflowDesk } from '@/components/Mediaflow/useMediaflowDesk'
import { parseScreenResolution, overlayRows, cueRemainingSec, formatCountdown, outputHealth, mediaSubtitle } from '@/components/Mediaflow/mediaflowMeta'
import { showModal } from '@/Utils/modalBus'

/**
 * Pop-out control window, one screen without page scroll (lg+): a header row,
 * Preview | rail | Program sized to the window, and Scenes · Sources ·
 * Graphics · Breaks filling the rest, each scrolling on its own. Talks to the
 * main tab over a BroadcastChannel and polls the production like the workspace
 * does. Closing it changes nothing on the server — it is just another remote.
 * Sources can be ordered, shown / hidden and mixed here; creating, renaming and
 * deleting, adding or editing sources, settings and automation stay in the
 * main window.
 */
const store = useStore()
const route = useRoute()
const { notify } = useNotification()
const canEditCanvas = useMatchMedia('(min-width: 768px)')
const isDesktop = useMatchMedia('(min-width: 1024px)')

const id = computed(() => String(route.params.id || ''))
const mediaflow = computed(() => store.getters['Mediaflows/mediaflow'])
const ready = computed(() => String(mediaflow.value?._id || '') === id.value)
const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])
const sessionActive = computed(() => !!mediaflow.value?.obsInstance)
const canvas = computed(() => parseScreenResolution(mediaflow.value?.screenResolution, mediaflow.value?.resolutionPreset))

const loading = ref(true)
const loadError = ref('')
const enabled = computed(() => !!mediaflow.value?.enabled)
let alive = true
let pollTimer = null
const now = ref(Date.now())
let clock = null

// Escape closes this page's modals even from a focused field (see ModalBox).
provide('modalEscapeFromFields', true)
provide('modalPlainBackdrop', true)

const desk = useMediaflowDesk({
	mediaflow,
	store,
	notify,
	sessionActive,
	channelName: `castr-mediaflow-${id.value}`,
})
const layouts = desk.layouts
const cue = desk.cue
const sourceActions = useMediaflowSourceActions({ mediaflow, store, notify, desk })
const maxMediaflowMedia = computed(() => store.getters['User/maxMediaflowMedia'])
const sourceSubtitle = (media) => mediaSubtitle(media, allInOneStreams.value)

const destinationStream = computed(() => {
	const d = mediaflow.value?.destination
	const destId = d && typeof d === 'object' ? d._id : d
	return destId ? allInOneStreams.value.find((s) => s._id === destId) || null : null
})

// ---------------------------------------------------------------- output pulse (Program → Output)
const pulse = ref(null)
const lastAliveAt = ref(null)
let pulseTimer = null
const pollPulse = async () => {
	if (!alive) return
	const key = mediaflow.value?.key
	if (key && enabled.value) {
		try {
			pulse.value = await StreamStatsService.getStreamMediaPulse(`${key}_preview`)
			if (pulse.value?.alive) lastAliveAt.value = Date.now()
		} catch (e) { /* keep polling */ }
	} else pulse.value = null
	if (alive) pulseTimer = setTimeout(pollPulse, document.hidden ? 10000 : 5000)
}
const health = computed(() => outputHealth(pulse.value, { sessionActive: sessionActive.value, enabled: enabled.value, lastAliveAt: lastAliveAt.value, now: now.value }))

const refresh = async () => {
	if (!alive || !id.value) return
	try {
		await store.dispatch('Mediaflows/getMediaflow', id.value)
	} catch (err) {
		console.error('popout refresh failed', err)
	}
	if (alive) pollTimer = setTimeout(refresh, document.hidden ? 20000 : 10000)
}

const load = async () => {
	loading.value = true
	loadError.value = ''
	try {
		// App.vue skips its bootstrap on fullScreen routes, so do the minimum here.
		await store.dispatch('Auth/login')
		await store.dispatch('User/loadUser')
		await store.dispatch('Streams/getStreams')
		await store.dispatch('Mediaflows/getMediaflow', id.value)
		if (!desk.editingFlowId.value) desk.editingFlowId.value = desk.restorePreviewId()
		pollTimer = setTimeout(refresh, 10000)
		void pollPulse()
	} catch (err) {
		console.error(err)
		loadError.value = err?.message || 'Could not open this production.'
	} finally {
		loading.value = false
	}
}

watch(() => mediaflow.value?.name, (name) => { document.title = name ? `${name} · Control · Castr` : 'Control · Castr' }, { immediate: true })

// ---------------------------------------------------------------- go live / stop
const startProcessing = ref(false)
const startBlockedReason = computed(() => {
	if (!enabled.value) return 'Enable the production first.'
	if (!layouts.value.length) return 'Add a scene first.'
	if (!mediaflow.value?.destination) return 'Choose a destination first.'
	return ''
})
const onStart = async () => {
	startProcessing.value = true
	try {
		const res = await MediaflowService.startStream(mediaflow.value._id)
		notify({ group: 'success', text: "You're live. It keeps running if you close this window." })
		if (res?.data) {
			store.dispatch('Mediaflows/setObsInstance', {
				mediaflowId: mediaflow.value._id,
				obsInstance: res.data.id,
				flowInstanceReservedAt: res.data.flowInstanceReservedAt,
				flowInstanceReserveTimeSec: res.data.flowInstanceReserveTimeSec,
			})
		}
		desk.post({ type: 'refresh' })
	} catch (err) {
		console.error(err)
		notify({ group: 'error', text: err?.message || err || 'Unable to go live.' })
	}
	startProcessing.value = false
}
const onStop = async () => {
	const mediaflowId = mediaflow.value._id
	startProcessing.value = true
	try {
		await MediaflowService.stopStream(mediaflowId)
		notify({ group: 'success', text: 'Production stopped' })
		store.dispatch('Mediaflows/clearObsInstance', mediaflowId)
		store.dispatch('Mediaflows/setCue', { mediaflowId, cue: null })
		desk.post({ type: 'refresh' })
	} catch (err) {
		console.error(err)
		notify({ group: 'error', text: `Couldn't stop the production: ${err?.message || 'please try again.'}` })
		store.dispatch('Mediaflows/getMediaflow', mediaflowId).catch(() => {})
	}
	startProcessing.value = false
}

// ---------------------------------------------------------------- send to live
const pendingSendFlowId = ref(null)
const doSend = async () => {
	const flowId = pendingSendFlowId.value || desk.editingFlowId.value
	pendingSendFlowId.value = null
	if (flowId) await desk.sendToLive(flowId)
}
const onSendToLive = (flow = desk.editingFlow.value) => {
	if (!enabled.value || !flow || flow.active || flow.kind === 'break') return
	pendingSendFlowId.value = flow._id
	if (desk.hasOnAirDraft.value) { showModal('popout-confirm-send'); return }
	void doSend()
}
const sendDisabledReason = computed(() => {
	if (!enabled.value) return 'Enable the production first.'
	if (!desk.editingFlow.value) return 'Pick a scene first.'
	if (desk.editingFlow.value.kind === 'break') return 'Cue breaks from the Breaks dock.'
	if (desk.editingIsOnAir.value) return 'Pick another scene to send live'
	return ''
})
const status = computed(() => (!enabled.value ? 'disabled' : sessionActive.value ? 'live' : 'offline'))
const cueRemaining = computed(() => cueRemainingSec(cue.value, now.value))

// ---------------------------------------------------------------- one-screen layout
// Hide Program: Preview takes the full width (remembered).
const PROGRAM_KEY = 'castr-mf-popout-program'
const showProgram = ref((() => { try { return localStorage.getItem(PROGRAM_KEY) !== 'hidden' } catch (e) { return true } })())
const toggleProgram = () => {
	showProgram.value = !showProgram.value
	try { localStorage.setItem(PROGRAM_KEY, showProgram.value ? 'shown' : 'hidden') } catch (e) { /* private mode */ }
}

// Monitors are sized from the window: the widest video that leaves the
// header, both monitor headers/toolbars and ≥ 160 px of docks on screen.
const RAIL_W = 104
const GAP = 12
const PAD_X = 32
// py-3 (24) + header (36) + 2 section gaps (24) + monitor header (32) + toolbar (36) + 2 row gaps (16) + docks (160)
const CHROME_Y = 24 + 36 + 24 + 32 + 36 + 16 + 160
const vw = ref(typeof window !== 'undefined' ? window.innerWidth : 1280)
const vh = ref(typeof window !== 'undefined' ? window.innerHeight : 800)
const onResize = () => { vw.value = window.innerWidth; vh.value = window.innerHeight }
const monitorWidth = computed(() => {
	const ratio = canvas.value.width && canvas.value.height ? canvas.value.width / canvas.value.height : 16 / 9
	const columns = showProgram.value ? 2 : 1
	const byWidth = (vw.value - PAD_X - RAIL_W - GAP * columns) / columns
	const byHeight = (vh.value - CHROME_Y) * ratio
	return Math.max(200, Math.floor(Math.min(byWidth, byHeight)))
})
const monitorsStyle = computed(() => (isDesktop.value
	? { gridTemplateColumns: showProgram.value ? `${monitorWidth.value}px ${RAIL_W}px ${monitorWidth.value}px` : `${monitorWidth.value}px ${RAIL_W}px` }
	: null))

const onKeydown = (event) => {
	if (event.defaultPrevented) return
	const meta = event.metaKey || event.ctrlKey
	if (event.target?.closest?.('input, textarea, select, [contenteditable="true"], [role="dialog"], .sidebar-container, .dropdown-wrapper')) return
	// The canvas keeps plain keys (nudging); ⌘ combos still reach us from it.
	if (!meta && event.target?.closest?.('[role="application"]')) return
	if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
	if (meta && (event.key === 'z' || event.key === 'Z')) {
		if (!desk.draftMode.value && !desk.pendingCount.value) return
		event.preventDefault()
		if (event.shiftKey) desk.redo(); else desk.undo()
		return
	}
	if (meta && event.key === 'Enter') {
		if (!desk.pendingCount.value || desk.applying.value) return
		event.preventDefault()
		void desk.applyDraft()
		return
	}
	if (meta || event.altKey) return
	if (event.key === ' ' || event.key === 'Enter') {
		if (event.target?.closest?.('button, a, [tabindex]')) return
		if (sendDisabledReason.value) return
		event.preventDefault(); onSendToLive(); return
	}
	if (event.key === 'b' || event.key === 'B') {
		if (cue.value) { event.preventDefault(); void desk.endBreak() }
		return
	}
	const digit = /^Digit([1-9])$/.exec(event.code)?.[1] || (/^[1-9]$/.test(event.key) ? event.key : null)
	if (!digit) return
	if (event.shiftKey) {
		const overlay = overlayRows(desk.overlays.value).flatMap((r) => r.items)[Number(digit) - 1]
		if (overlay && desk.capabilities.value.overlays && enabled.value) { event.preventDefault(); void desk.takeOverlay(overlay) }
		return
	}
	const flow = layouts.value[Number(digit) - 1]
	if (flow) desk.selectFlow(flow._id)
}

onMounted(() => {
	window.addEventListener('keydown', onKeydown)
	window.addEventListener('resize', onResize)
	clock = setInterval(() => { now.value = Date.now() }, 1000)
	void load()
})
onBeforeUnmount(() => {
	alive = false
	clearTimeout(pollTimer)
	clearTimeout(pulseTimer)
	clearInterval(clock)
	window.removeEventListener('keydown', onKeydown)
	window.removeEventListener('resize', onResize)
})
</script>

<template>
	<div class="popout min-h-screen w-full bg-surface-1 px-4 py-3 text-surface-text-1 lg:h-screen lg:overflow-hidden" data-testid="preview-popout">
		<div v-if="loading" class="flex min-h-[60vh] items-center justify-center" aria-busy="true">
			<Spinner text="Opening your production…" classes="text-surface-8" spinner-color="var(--c-dark-8)" spinner-size="22px" />
		</div>
		<EmptyState v-else-if="loadError || !ready" title="Couldn't open the control window" :subtitle="loadError || 'This production is not available.'">
			<template #actions>
				<FormButton type="secondary" size="md" @click="load">Try again</FormButton>
			</template>
		</EmptyState>

		<main v-else class="flex min-h-0 flex-col gap-3 lg:h-full">
			<!-- Header row -->
			<header class="flex h-9 shrink-0 items-center gap-3" data-testid="popout-header">
				<span class="flex size-7 shrink-0 items-center justify-center rounded-full bg-main-transparent text-main-light">
					<icon-base width="14" height="14" fill="none"><icon-cloud /></icon-base>
				</span>
				<h1 class="m-0 min-w-0 truncate text-base font-semibold text-surface-text-2">{{ mediaflow.name }}</h1>
				<StatusPill pill class="shrink-0" :variant="status === 'live' ? 'success' : 'muted'" :pulse="status === 'live'" data-testid="popout-status">
					{{ status === 'live' ? 'Live' : status === 'disabled' ? 'Disabled' : 'Offline' }}
				</StatusPill>
				<p class="m-0 hidden min-w-0 truncate text-xs text-surface-8 md:block">
					<template v-if="desk.onAirFlow.value">{{ sessionActive ? 'On air' : 'Starts with' }}: <span class="text-surface-text-1">{{ desk.onAirFlow.value.name }}</span></template>
					<template v-if="cue && cueRemaining !== null"> · back in <span class="font-mono text-surface-text-1">{{ formatCountdown(cueRemaining) }}</span></template>
				</p>
				<span class="ml-auto flex shrink-0 items-center gap-2">
					<FormButton v-if="cue" type="red" size="sm" class="font-semibold" :is-loading="desk.cueing.value" data-testid="popout-end-break" @click="desk.endBreak()">End break</FormButton>
					<FormButton
						type="link"
						size="sm"
						class="hidden !px-2 text-xs text-surface-8 hover:text-white lg:inline-flex"
						:aria-pressed="showProgram"
						data-testid="popout-toggle-program"
						@click="toggleProgram"
					>{{ showProgram ? 'Hide Program' : 'Show Program' }}</FormButton>
					<template v-if="enabled">
						<FormButton v-if="sessionActive" type="red" size="sm" class="!px-4 font-semibold" :is-loading="startProcessing" data-testid="popout-stop" @click="showModal('popout-confirm-stop')">Stop</FormButton>
						<Tooltip v-else-if="startBlockedReason" wrap-trigger :content="startBlockedReason" wrapper-class="inline-flex">
							<FormButton type="main" size="sm" class="!px-4 font-semibold" disabled data-testid="popout-go-live">Go live</FormButton>
						</Tooltip>
						<FormButton v-else type="main" size="sm" class="!px-4 font-semibold" :is-loading="startProcessing" data-testid="popout-go-live" @click="showModal('popout-confirm-go-live')">Go live</FormButton>
					</template>
				</span>
			</header>

			<!-- Monitors row: Preview | rail | Program -->
			<section
				class="grid min-w-0 shrink-0 grid-cols-1 gap-x-3 gap-y-2 lg:justify-center lg:grid-rows-[auto_auto_auto_auto]"
				:style="monitorsStyle"
				aria-label="Monitors"
				data-testid="popout-monitors"
			>
				<MediaflowEditorPanel
					:desk="desk"
					:canvas="canvas"
					:streams="allInOneStreams"
					:session-active="sessionActive"
					:enabled="enabled"
					:interactive="canEditCanvas"
					:send-disabled-reason="sendDisabledReason"
					popout
					@send="onSendToLive()"
				/>
				<MediaflowTransitionRail
					class="lg:col-start-2 lg:row-start-2"
					:desk="desk"
					:session-active="sessionActive"
					:disabled-reason="sendDisabledReason"
					:enabled="enabled"
					@send="onSendToLive()"
				/>
				<div class="hidden items-center justify-center lg:col-start-2 lg:row-start-3 lg:flex">
					<MediaflowShortcuts />
				</div>
				<MediaflowMonitor
					v-if="showProgram || !isDesktop"
					label="Program"
					:mediaflow="mediaflow"
					:destination-stream="destinationStream"
					:media-pulse="pulse"
					:session-active="sessionActive"
					:health="health"
					:on-air-flow="desk.onAirFlow.value"
					:cue="cue"
					:now="now"
					:flows="mediaflow.flows || []"
					:ending="desk.cueing.value"
					:canvas="canvas"
					:streams="allInOneStreams"
					:overlays="desk.overlays.value"
					@end-break="desk.endBreak()"
				/>
			</section>

			<!-- Docks fill what is left; each scrolls on its own -->
			<section class="grid min-h-0 min-w-0 grid-cols-1 gap-3 md:grid-cols-2 lg:min-h-[160px] lg:flex-1 lg:grid-cols-4" aria-label="Docks" data-testid="popout-docks">
				<MediaflowScenesDock
					class="md:h-[280px] lg:h-full"
					:desk="desk"
					:enabled="enabled"
					:session-active="sessionActive"
					read-only
					@select="desk.selectFlow($event._id)"
					@send-to-live="onSendToLive"
				/>
				<MediaflowSourcesDock
					v-if="canEditCanvas"
					class="md:h-[280px] lg:h-full"
					:flow="desk.previewFlow.value"
					:draft-state-for="desk.mediaDraftState"
					:audio-live="desk.editingIsOnAir.value"
					:max-media="maxMediaflowMedia"
					:enabled="enabled"
					:subtitle-for="sourceSubtitle"
					:selected-media-id="desk.selectedMediaId.value"
					read-only
					@select="desk.selectMedia($event.id)"
					@position="desk.selectMedia($event.id)"
					@reorder="sourceActions.reorder"
					@toggle-show="sourceActions.toggleShow"
					@toggle-mute="sourceActions.toggleMute"
					@volume="sourceActions.setVolume"
					@audio-sync="sourceActions.setAudioSync"
				/>
				<MediaflowGraphicsDock
					class="md:h-[280px] lg:h-full"
					:desk="desk"
					:canvas="canvas"
					:enabled="enabled"
					:session-active="sessionActive"
					read-only
					@toggle="desk.takeOverlay($event)"
					@position="desk.selectOverlay($event._id)"
				/>
				<MediaflowBreaksDock
					class="md:h-[280px] lg:h-full"
					:desk="desk"
					:canvas="canvas"
					:enabled="enabled"
					:session-active="sessionActive"
					:now="now"
					read-only
					@cue="desk.cueBreak($event)"
					@end="desk.endBreak()"
					@edit-layout="desk.selectFlow($event._id)"
				/>
			</section>
		</main>

		<ConfirmModal
			modal-id="popout-confirm-send"
			title="Unsaved changes on air"
			:message="`${desk.pendingCount.value || 'Some'} ${desk.pendingCount.value === 1 ? 'change is' : 'changes are'} not applied yet. They stay a draft when you switch.`"
			ok-text="Keep draft and switch"
			cancel-text="Cancel"
			modal-type="primary"
			@modal-confirm="doSend"
		/>
		<ConfirmModal
			modal-id="popout-confirm-go-live"
			title="Go live?"
			:message="`${desk.onAirFlow.value ? `Starts with “${desk.onAirFlow.value.name}” and streams` : 'Streams'} to your destination until you stop it.`"
			ok-text="Go live"
			cancel-text="Not yet"
			modal-type="primary"
			@modal-confirm="onStart"
		/>
		<ConfirmModal
			modal-id="popout-confirm-stop"
			title="Stop the production?"
			message="Viewers lose the feed and automation pauses."
			ok-text="Stop production"
			cancel-text="Keep running"
			modal-type="danger"
			@modal-confirm="onStop"
		/>
	</div>
</template>
